import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant: 'assist' | 'filter' | 'input' | 'suggestion'
  selected?: boolean | undefined
  icon?: string | undefined
  /** Solo variant="input": mostra il pulsante di rimozione. */
  onRemove?: (() => void) | undefined
  /** Nome accessibile del pulsante di rimozione (default "Rimuovi"). */
  removeLabel?: string | undefined
  children: ReactNode
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { variant, selected, icon, onRemove, removeLabel = 'Rimuovi', className, children, type = 'button', ...rest },
  ref,
) {
  const isToggle = variant === 'filter'
  const on = Boolean(selected)
  const lead = isToggle && on ? 'check' : icon
  const cls = cx('md-chip', `md-chip--${variant}`, on && 'md-chip--selected', onRemove && 'md-chip--removable')
  const content = (
    <>
      {lead ? <Icon name={lead} size={18} /> : null}
      <span className="md-chip__label">{children}</span>
    </>
  )
  if (variant === 'input' && onRemove) {
    return (
      <span className={cx(cls, className)}>
        <button ref={ref} type={type} aria-pressed={selected} {...rest} className="md-chip__main md-state md-touch">
          {content}
        </button>
        <button
          type="button"
          className="md-chip__remove md-state"
          aria-label={`${removeLabel}: ${typeof children === 'string' ? children : ''}`.replace(/: $/, '')}
          onClick={onRemove}
        >
          <Icon name="close" size={18} />
        </button>
      </span>
    )
  }
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={isToggle ? on : selected}
      {...rest}
      className={cx(cls, 'md-chip--solo', 'md-state', 'md-touch', className)}
    >
      {content}
    </button>
  )
})

export type PillTone = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'info'

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone: PillTone
  icon?: string | undefined
}

/** Etichetta di stato non interattiva: sostituisce i `badge bg-*` di Bootstrap. */
export function StatusPill({ tone, icon, className, children, ...rest }: StatusPillProps) {
  return (
    <span {...rest} className={cx('md-pill', `md-pill--${tone}`, className)}>
      {icon ? <Icon name={icon} size={18} /> : null}
      {children}
    </span>
  )
}

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Senza value il badge e' un puntino di 6px. */
  value?: number | undefined
  max?: number | undefined
}

export function Badge({ value, max = 99, className, ...rest }: BadgeProps) {
  if (value === undefined) return <span {...rest} className={cx('md-badge', 'md-badge--dot', className)} />
  const text = value > max ? `${max}+` : String(value)
  return (
    <span {...rest} className={cx('md-badge', 'md-num', className)}>
      {text}
    </span>
  )
}
